import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";

import {
  callAgent,
  clearAgentSession,
  newSessionId,
  transcribeAudio,
} from "./agentClient";
import { StepCard, ResultTable } from "./StepCard";
import { ResultChart } from "./ResultChart";
import WidgetErrorBoundary from "./ErrorBoundary";
import {
  isRecordingSupported,
  isSpeechSupported,
  speakText,
  startRecording,
  stopSpeaking,
} from "./speech";
import {
  createSession,
  saveMessage,
  listSessions,
  getMessages,
  issueTicket,
  currentUserName,
} from "./chatStore";
import "./AskPineWidget.css";

const DOCK_WIDTH = 400;

/* A line that is entirely pipe-delimited cells, e.g.
   "| Fiscal Year Revenue | |---:| | 932,715,099.57 |".
   The analytics prompt forbids markdown tables, but the model still emits one
   occasionally, and react-markdown has no remark-gfm here so it would render
   as literal pipes. The result rows are drawn by <ResultTable> anyway, so
   dropping these lines loses nothing. */
const TABLE_LINE = /^\s*\|.*\|\s*$/;

function stripMarkdownTables(text) {
  if (typeof text !== "string" || !text.includes("|")) return text;
  const kept = text.split("\n").filter((line) => !TABLE_LINE.test(line));
  return kept.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/* fact_finding appends "**Sources**" followed by a numbered markdown list
   (fact_finding/agent.py). Inline in the thread that list is a wall of URLs,
   so it is split off here and rendered as a collapsed <details>. */
const SOURCES_HEADING = /\n\s*\*\*Sources\*\*\s*\n/;
const SOURCE_ITEM = /(\d+)\.\s*\[([\s\S]*?)\]\((https?:\/\/[^\s)]+)\)/g;

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function splitSources(text) {
  if (typeof text !== "string") return { body: text, sources: [] };

  const match = text.match(SOURCES_HEADING);
  if (!match) return { body: text, sources: [] };

  const body = text.slice(0, match.index).trim();
  const tail = text.slice(match.index + match[0].length);

  const sources = [];
  const seen = new Set();
  let item;
  SOURCE_ITEM.lastIndex = 0;
  while ((item = SOURCE_ITEM.exec(tail)) !== null) {
    const url = item[3];
    if (seen.has(url)) continue;
    seen.add(url);
    // Titles can wrap across lines in the markdown; flatten them.
    const title = item[2].replace(/\s+/g, " ").trim();
    sources.push({ n: Number(item[1]), title: title || hostOf(url), url });
  }

  // Nothing parsed means the tail was not the shape we expected — keep the
  // original text rather than silently dropping it.
  return sources.length ? { body, sources } : { body: text, sources: [] };
}

function SourceList({ sources }) {
  if (!sources.length) return null;
  return (
    <details className="ap-sources">
      <summary>
        {sources.length} source{sources.length === 1 ? "" : "s"}
      </summary>
      <ol>
        {sources.map((s) => (
          <li key={s.url}>
            <a href={s.url} target="_blank" rel="noreferrer noopener">
              {s.title}
            </a>
            {hostOf(s.url) ? <span className="ap-src-host">{hostOf(s.url)}</span> : null}
          </li>
        ))}
      </ol>
    </details>
  );
}

/* Header icons, taken from the agent-chat-widget prototype. Outline strokes
   rather than text glyphs: the glyphs they replace were being misread - a
   reload arrow for chat history, a tab character for docking.
   Sizing, stroke width and colour all come from .ap-iconbtn svg in the CSS. */
const ICON = {
  dock: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
      <path d="M14 4.5v15" />
    </svg>
  ),
  split: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
      <path d="M12 4.5v15" />
    </svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.8V12l3 1.8" />
    </svg>
  ),
  newChat: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
      <path d="M14.5 6.5l3 3" />
    </svg>
  ),
  collapse: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6.5 10l5.5 5.5L17.5 10" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 7l10 10M17 7L7 17" />
    </svg>
  ),
};

const SUGGESTIONS = [
  "How do I create a client?",
  "How many active roles do we have?",
  "Latest trends in executive search",
];

/* Models the agent service accepts. The picker that used to expose these was
   removed - see the composer bar - but the list stays so switching is a
   one-line change rather than a rebuild of the control.

   To switch: point ACTIVE_MODEL at another id below.
   To bring the picker back: restore the <select> in .ap-bar and swap
   ACTIVE_MODEL for the model/setModel state it used. */
const MODELS = [
  { id: "gpt-5.4", label: "GPT-5.4" },
  { id: "gpt-5.4-mini", label: "GPT-5.4 Mini" },
  { id: "gpt-4.1", label: "GPT-4.1" },
  { id: "claude-opus-4-6", label: "Claude Opus 4.6" },
  { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
];

/* GPT-5.4 held the knowledge base where the others did not: 4.1 returned prose
   instead of JSON and counted closures off rolestatus_id, which the schema
   explicitly forbids. Everyone gets the same model so two people asking the
   same question cannot get different numbers. */
const ACTIVE_MODEL = MODELS[0].id;

/* Greeting, personalised when the browser knows who is logged in. Falls back
   to the plain wording when it does not - a private window, cleared storage. */
function buildGreeting() {
  const name = currentUserName();
  const first = name ? name.split(" ")[0] : "";
  return {
    role: "agent",
    text:
      (first ? "Hi " + first + " - I can " : "I can ") +
      "answer questions about using Pine, run data queries against the Pine " +
      "database, and research the external market. What do you need?",
  };
}

/**
 * Ask Pine agent widget. Mounted once in MainLayout so it is available on every
 * authenticated screen.
 *
 * `token` is the Pine JWT. It is accepted but NOT transmitted yet — the agent
 * service does not authenticate against Pine today. It is needed the moment we
 * add the candidate-search route, which proxies to the Node backend on the
 * user's behalf.
 */
function AskPineWidget({ token }) {
  const [mode, setMode] = useState("pill");   // 'pill' | 'float' | 'dock' | 'split'
  const [messages, setMessages] = useState([buildGreeting()]);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // Index of the message currently being read aloud, or null. Only one can
  // speak at a time, so a single index is enough.
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [recording, setRecording] = useState(false);
  const [micError, setMicError] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const [down, setDown] = useState(false);
  // Fixed, not chosen. Kept as a variable so every call site stays unchanged
  // if the picker ever comes back.
  const model = ACTIVE_MODEL;
  const [threads, setThreads] = useState(null);   // null = panel closed
  const [loadingThreads, setLoadingThreads] = useState(false);

  const sessionRef = useRef(newSessionId());
  const runRef = useRef(null);       // in-flight { promise, cancel }
  const logRef = useRef(null);
  const inputRef = useRef(null);
  // Brief most recently handed to the Candidate Search page. Only a search we
  // started gets a follow-up from us — a search the user ran by hand is theirs.
  const handedOffRef = useRef(null);
  /* Promise for this thread's Pine chat_sessions row. A promise rather than an
     id so that concurrent saves share one creation instead of racing to make
     two sessions. Created lazily on the first message actually sent, never on
     mount: the AskPine page creates one per page load, which is why 285 of its
     331 rows have no messages in them. */
  const threadRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const screen = (location.pathname || "/").replace(/^\//, "") || "home";

  /* ---- shell reflow so the panel never covers page content ---- */
  useEffect(() => {
    const shell = document.querySelector(".main-wrapper");
    if (!shell) return undefined;
    shell.style.transition = "padding-right .22s ease";
    shell.style.paddingRight =
      mode === "split" ? "50vw" : mode === "dock" ? `${DOCK_WIDTH}px` : "";
    return () => {
      shell.style.paddingRight = "";
    };
  }, [mode]);

  /* ---- keyboard: Cmd/Ctrl+K toggles, Esc collapses ---- */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setMode((m) => (m === "pill" ? "float" : "pill"));
      }
      if (e.key === "Escape") setMode((m) => (m === "pill" ? m : "pill"));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ---- close any in-flight socket on unmount ---- */
  useEffect(() => () => runRef.current?.cancel(), []);

  /* ---- autoscroll + focus ---- */
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    if (mode !== "pill") inputRef.current?.focus();
  }, [mode]);

  /* The Candidate Search page reports what our brief actually found. The turn
     has already ended by then, so this is the only way the thread learns the
     search came back empty. Fixed copy, no model call — it is instant, costs
     nothing, and cannot invent a suggestion that does not help. */
  useEffect(() => {
    const onResult = (event) => {
      const { query, count } = event.detail || {};
      const handed = handedOffRef.current;
      if (!handed || query !== handed) return; // not our search
      handedOffRef.current = null;             // report each hand-off once
      if (count > 0) return;

      setMessages((m) => [
        ...m,
        {
          role: "agent",
          text:
            "That brief didn't match any candidates. " +
            "Try rephrasing it, or simplifying it - briefs with fewer " +
            "requirements usually match more people.",
          route: "candidate_search",
        },
      ]);
      setInput(handed);
    };

    window.addEventListener("askpine:search-result", onResult);
    return () => window.removeEventListener("askpine:search-result", onResult);
  }, []);

  // Press to record, press again to stop and transcribe. The text lands in the
  // input box for the user to check - it is never sent on its own. Names and
  // metric words come back wrong often enough that an auto-sent question would
  // look like the agent had misunderstood.
  const toggleRecording = useCallback(async () => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      recorderRef.current = null;
      setRecording(false);
      return;
    }

    try {
      const handle = await startRecording({
        onBlob: async (blob) => {
          setTranscribing(true);
          try {
            const text = await transcribeAudio(blob, token);
            if (text) {
              setInput((prev) => (prev ? prev.trim() + " " + text : text));
              if (inputRef.current) inputRef.current.focus();
            }
          } catch (err) {
            setMicError(err.message || "Could not transcribe that.");
          } finally {
            setTranscribing(false);
          }
        },
        onError: () => {
          setRecording(false);
          recorderRef.current = null;
          setMicError("Recording failed. Please type instead.");
        },
      });
      recorderRef.current = handle;
      setRecording(true);
      setMicError("");
    } catch {
      // Denied, or no microphone. Both look the same to the browser.
      setMicError("Microphone unavailable. Check the browser permission.");
    }
  }, [token]);

  const submit = useCallback(
    async (raw, forceRoute = null) => {
      const text = (raw || "").trim();
      if (!text || busy) return;

      setInput("");
      setBusy(true);
      setDown(false);

      // one steps card per turn, addressed by id as events arrive
      const turnId = newSessionId();
      setMessages((m) => [
        ...m,
        { role: "user", text },
        { role: "steps", id: turnId, steps: [], open: true, route: null, sql: null },
      ]);

      // Find the card by its stable id, never by object identity — each update
      // replaces the object, so a reference lookup only ever matches once.
      const bump = (mutate) =>
        setMessages((m) => {
          const i = m.findIndex((x) => x.role === "steps" && x.id === turnId);
          if (i === -1) return m;
          const next = [...m];
          next[i] = mutate({ ...next[i] });
          return next;
        });

      /* Persist the exchange. Every call fails soft — history is worth having,
         but never at the cost of the answer the user is waiting for. */
      const persist = async (role, content) => {
        if (!token || !content) return;
        if (!threadRef.current) threadRef.current = createSession(token);
        const threadId = await threadRef.current;
        if (threadId) await saveMessage(token, threadId, role, content);
      };

      persist("user", text);

      /* Permission ticket for this turn. Opaque and short-lived: the agent
         resolves it with Pine's backend rather than trusting anything sent
         from here. Fetched per turn because it expires in minutes. */
      const ticket = await issueTicket(token);

      const run = callAgent(text, {
        sessionId: sessionRef.current,
        history,
        model,
        forceRoute,
        ticket,
        userName: currentUserName(),
        onStep: (s) =>
          bump((card) => ({
            ...card,
            steps: [...card.steps, s],
            route: s.key === "classified" ? s.detail || card.route : card.route,
            sql: s.sql || card.sql,
          })),
        onAnswer: (payload) => {
          bump((card) => ({ ...card, open: false, route: card.route || payload.category }));
          setMessages((m) => [
            ...m,
            // graph travels with the rows it describes. Kept on the same
            // message because its x and y name columns in that exact result -
            // a chart paired with a later table would plot the wrong numbers.
            ...(payload.data
              ? [{ role: "table", data: payload.data, graph: payload.graph || null }]
              : []),
            {
              role: "agent",
              text: payload.answer || "",
              route: payload.category,
              qa: payload.qa || null,
              choice: payload.choice || null,
              elapsed: payload.elapsed_seconds,
            },
          ]);
          setHistory((h) =>
            [...h, { role: "user", content: text }, { role: "assistant", content: payload.answer || "" }].slice(-20)
          );

          persist("assistant", payload.answer || "");

          // An agent route can ask the app to open a Pine screen and run the
          // request there (e.g. candidate_search -> /UploadJd). The page owns
          // the actual search; we just hand it the prepared query.
          const nav = payload.navigate;
          if (nav?.to) {
            if (nav.param) {
              // Universal Search reads its term from the query string, so the
              // URL itself is the hand-off — no state, no field to fill.
              navigate(`${nav.to}?${nav.param}=${encodeURIComponent(nav.query || "")}`);
            } else {
              handedOffRef.current = nav.query || "";
              navigate(nav.to, {
                state: { agentQuery: nav.query, agentAt: Date.now() },
              });
            }
          }
        },
        onError: (msg) => {
          bump((card) => ({ ...card, open: false }));
          setMessages((m) => [...m, { role: "agent", text: `⚠ ${msg}`, error: true }]);
          setDown(true);
        },
      });

      runRef.current = run;
      await run.promise;
      runRef.current = null;
      setBusy(false);
    },
    [busy, history, model, navigate]
  );

  /* ---- saved conversations ---- */

  const toggleThreads = async () => {
    if (threads !== null) {
      setThreads(null);
      return;
    }
    setLoadingThreads(true);
    setThreads(await listSessions(token));
    setLoadingThreads(false);
  };

  const openThread = async (sessionId) => {
    setThreads(null);
    runRef.current?.cancel();
    setBusy(true);

    const rows = await getMessages(token, sessionId);

    /* Continue the stored conversation rather than starting a parallel one:
       further messages append to this same session. */
    threadRef.current = Promise.resolve(sessionId);

    /* Derive the ai-ml session id from the stored thread, so reopening a
       conversation reconnects to the agent that was handling it. That memory
       only survives while ai-ml is running — after a restart the thread reads
       back correctly but the agent starts blank. */
    sessionRef.current = `pine-thread-${sessionId}`;

    const restored = rows.map((r) => ({
      role: r.role === "user" ? "user" : "agent",
      text: r.content || "",
    }));

    setMessages(restored.length ? restored : [buildGreeting()]);
    setHistory(
      rows
        .map((r) => ({
          role: r.role === "user" ? "user" : "assistant",
          content: r.content || "",
        }))
        .slice(-20),
    );
    setBusy(false);
  };

  const newThread = () => {
    runRef.current?.cancel();
    clearAgentSession(sessionRef.current, token);
    sessionRef.current = newSessionId();
    threadRef.current = null;   // next message starts a new stored conversation
    setMessages([buildGreeting()]);
    setHistory([]);
    setBusy(false);
    setDown(false);
  };

  /* ---------------------------------------------------------------- */

  if (mode === "pill") {
    return (
      <button
        type="button"
        className="ap-pill"
        aria-expanded="false"
        onClick={() => setMode("float")}
      >
        <span className="ap-mark ap-mono">P</span>
        <span className="ap-lbl">
          <b>Ask Pine</b>
          <small className="ap-mono">Ctrl+K</small>
        </span>
      </button>
    );
  }

  return (
    <section className="ap-panel" data-mode={mode} role="dialog" aria-label="Ask Pine">
      <div className="ap-head">
        <div className="ap-mark ap-mono">P</div>
        <div>
          <h2>Ask Pine</h2>
          <div className="ap-status ap-mono" data-state={down ? "down" : ""}>
            {down ? "DISCONNECTED" : busy ? "WORKING…" : "READY"}
          </div>
        </div>
        <div className="ap-acts">
          <button
            type="button" className="ap-iconbtn" title="Dock to side"
            aria-label="Dock to side"
            aria-pressed={mode === "dock"}
            onClick={() => setMode((m) => (m === "dock" ? "float" : "dock"))}
          >{ICON.dock}</button>
          <button
            type="button" className="ap-iconbtn" title="Split screen"
            aria-label="Split screen"
            aria-pressed={mode === "split"}
            onClick={() => setMode((m) => (m === "split" ? "float" : "split"))}
          >{ICON.split}</button>
          <button
            type="button" className="ap-iconbtn" title="Chat history"
            aria-label="Chat history"
            aria-pressed={threads !== null} onClick={toggleThreads}
          >{ICON.history}</button>
          <button
            type="button" className="ap-iconbtn" title="New chat"
            aria-label="New chat" onClick={newThread}
          >{ICON.newChat}</button>
          <button
            type="button" className="ap-iconbtn" title="Collapse"
            aria-label="Collapse" onClick={() => setMode("pill")}
          >{ICON.collapse}</button>
        </div>
      </div>

      {threads !== null && (
        <div className="ap-threads">
          <div className="ap-threads-head">
            <span>Saved conversations</span>
            <button
              type="button" className="ap-iconbtn"
              onClick={() => setThreads(null)}
              title="Close history" aria-label="Close history"
            >{ICON.close}</button>
          </div>
          {loadingThreads ? (
            <p className="ap-threads-empty">Loading...</p>
          ) : threads.length === 0 ? (
            <p className="ap-threads-empty">No saved conversations yet.</p>
          ) : (
            <ul>
              {threads.map((t) => (
                <li key={t.session_id}>
                  <button type="button" onClick={() => openThread(t.session_id)}>
                    <b>{t.first_message || "Untitled"}</b>
                    <span>
                      {t.message_count} message{t.message_count === 1 ? "" : "s"}
                      {t.last_active ? ` · ${String(t.last_active).slice(0, 10)}` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="ap-log" ref={logRef}>
        {messages.map((m, i) => {
          if (m.role === "steps") {
            return <StepCard key={i} steps={m.steps} route={m.route} open={m.open} />;
          }
          if (m.role === "table") {
            // Chart above the rows it describes, never instead of them. A chart
            // cannot be copied into a spreadsheet or checked figure by figure;
            // the table is what makes the answer auditable. ResultChart returns
            // null whenever it cannot draw the data honestly.
            return (
              <React.Fragment key={i}>
                <ResultChart data={m.data} graph={m.graph} />
                <ResultTable data={m.data} />
              </React.Fragment>
            );
          }
          if (m.role === "user") {
            return (
              <div key={i} className="ap-msg ap-user">
                <div className="ap-bubble">{m.text}</div>
              </div>
            );
          }
          const { body, sources } = splitSources(m.text);
          return (
            <div key={i} className="ap-msg">
              <div className="ap-bubble">
                <ReactMarkdown>{stripMarkdownTables(body)}</ReactMarkdown>
                <SourceList sources={sources} />
                {isSpeechSupported() && body && (
                  // speakText must be reached inside the gesture itself -
                  // awaiting anything first makes iOS refuse to speak.
                  <button
                    type="button"
                    className={
                      speakingIdx === i ? "ap-speak ap-speak-on" : "ap-speak"
                    }
                    title={speakingIdx === i ? "Stop" : "Read aloud"}
                    aria-label={
                      speakingIdx === i
                        ? "Stop reading"
                        : "Read this answer aloud"
                    }
                    onClick={() => {
                      if (speakingIdx === i) {
                        stopSpeaking();
                        setSpeakingIdx(null);
                        return;
                      }
                      const started = speakText(body, () =>
                        setSpeakingIdx(null),
                      );
                      setSpeakingIdx(started ? i : null);
                    }}
                  >
                    {speakingIdx === i ? "⏹" : "🔊"}
                  </button>
                )}
                {m.choice && !m.choiceTaken && (
                  <div className="ap-choice">
                    {(m.choice.options || []).map((opt) => (
                      <button
                        // Keyed on the label, not the route - every option
                        // carries the same route, so a route key collided and
                        // React rendered one button for the whole set.
                        key={opt.label}
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          // Mark this card resolved first so the buttons cannot
                          // be clicked twice, then re-run the question with the
                          // chosen name substituted in. Resubmitting the
                          // original text reproduced the same ambiguity and
                          // asked again, with no way out.
                          setMessages((prev) =>
                            prev.map((msg, j) =>
                              j === i ? { ...msg, choiceTaken: opt.route } : msg,
                            ),
                          );
                          submit(opt.refine || m.choice.query, opt.route);
                        }}
                      >
                        <b>{opt.label}</b>
                        {opt.hint ? <span>{opt.hint}</span> : null}
                      </button>
                    ))}
                  </div>
                )}
                {/* The route and the confidence rating are gone from here.
                    Both described the machinery rather than the answer.

                    "q_and_a" is our routing vocabulary - it means nothing to a
                    consultant and shows how the service is partitioned.

                    "conf: high" was worse: it is self-reported and
                    uncalibrated. It read 10/10 on a query that returned no rows
                    and on revenue figures that were wrong. A confidence badge
                    the reader can see is a claim the system has not earned, and
                    a green HIGH beside a wrong answer is worse than no badge.

                    category_name stays. It names the part of the knowledge base
                    the answer came from, so the reader can judge the source. */}
                {(m.qa?.category_name || m.elapsed != null) && (
                  <div className="ap-meta">
                    {m.qa?.category_name && <span className="ap-tag">{m.qa.category_name}</span>}
                    {m.elapsed != null && <span className="ap-tag">{m.elapsed}s</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="ap-foot">
        {messages.length < 3 && (
          <div className="ap-chips">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" className="ap-chip" onClick={() => submit(s)}>
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="ap-composer">
          {micError && (
            <div className="ap-mic-error" onClick={() => setMicError("")}>
              {micError}
            </div>
          )}
          <textarea
            ref={inputRef}
            rows={2}
            value={input}
            placeholder="Ask about Pine, your data, or the market…"
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
          />
          <div className="ap-bar">
            {/* The model picker was removed after testing: GPT-5.4 answered
                correctly where the others did not, and letting users choose
                meant two people could get different numbers for the same
                question. MODELS and the model state are deliberately kept so
                switching later is a one-line change - see ACTIVE_MODEL. */}
            <span className="ap-ctx ap-mono">{screen}</span>
            {isRecordingSupported() && (
              <button
                type="button"
                className={recording ? "ap-mic ap-mic-on" : "ap-mic"}
                title={
                  recording
                    ? "Stop and transcribe"
                    : transcribing
                      ? "Transcribing…"
                      : "Speak your question"
                }
                aria-label="Record a spoken question"
                disabled={busy || transcribing}
                onClick={toggleRecording}
              >
                {recording ? "■" : transcribing ? "…" : "🎤"}
              </button>
            )}
            <button
              type="button" className="ap-send" title="Send"
              data-ready={input.trim() && !busy ? 1 : 0}
              onClick={() => submit(input)}
            >↑</button>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Exported wrapped, so a widget crash can never take the app down with it. */
export default function AskPineWidgetSafe(props) {
  return (
    <WidgetErrorBoundary>
      <AskPineWidget {...props} />
    </WidgetErrorBoundary>
  );
}
